import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';
import { ArrowLeftLine } from '@archipro-design/icons';

import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import {
    GateFieldError,
    GateFieldLabel,
    GateInput,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import {
    GateIdentityFields,
    type GateIdentity,
} from '~/modules-rocco/user/component/auth-modal/GateIdentityFields';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';
import type {
    BookingContact,
    BookingContactErrors,
} from '../domain/book-meeting-flow';

export interface BookMeetingGateContactProps {
    contact: BookingContact;
    onChange: (next: BookingContact) => void;
    errors: BookingContactErrors;
    /** The signed-in reader's account, summarised; null for a guest. */
    identity: GateIdentity | null;
    /** The email is the account's own and cannot be changed here. */
    emailLocked: boolean;
    /** One line naming the meeting, the person and the time already chosen. */
    summary: string;
    /** The time is booked into a calendar rather than asked for. */
    isBooked: boolean;
    isSending: boolean;
    /** Why the last send did not go through, in the route's words. */
    errorMessage?: string | null;
    onBack: () => void;
    onSubmit: () => void;
    onSwitchAccount?: () => void;
}

const FIELD_ORDER: (keyof BookingContact)[] = [
    'firstName',
    'lastName',
    'email',
    'phone',
];

/**
 * "Your details": who the meeting is for, asked last so the reader has
 * already chosen everything worth coming back to.
 *
 * A signed-in reader sees their account as one summary line, opened only when
 * something the booking needs is missing from it. A guest fills the fields,
 * and sending signs them in — an account is made for a new email, and an
 * existing one is asked to log in.
 */
export const BookMeetingGateContact = ({
    contact,
    onChange,
    errors,
    identity,
    emailLocked,
    summary,
    isBooked,
    isSending,
    errorMessage,
    onBack,
    onSubmit,
    onSwitchAccount,
}: BookMeetingGateContactProps) => {
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const incomplete = FIELD_ORDER.some((field) => !contact[field].trim());
    const [expanded, setExpanded] = useState(incomplete);
    const touchedRef = useRef(false);
    // Opens itself while the account is missing something the booking needs,
    // until the reader works the disclosure themselves.
    useEffect(() => {
        if (!touchedRef.current && incomplete) setExpanded(true);
    }, [incomplete]);
    const hasErrors = Object.keys(errors).length > 0;
    useEffect(() => {
        if (hasErrors) setExpanded(true);
    }, [hasErrors]);

    const set =
        (field: keyof BookingContact) =>
        (event: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, [field]: event.target.value });

    return (
        <div className="mx-auto flex w-full max-w-93.75 flex-1 flex-col">
            <button
                type="button"
                aria-label="Back to choosing a time"
                disabled={isSending}
                onClick={onBack}
                className="flex size-8 items-center justify-center self-start rounded-[0.75rem] text-[#181818] hover:bg-black/5 disabled:opacity-40"
            >
                <ArrowLeftLine className="size-5" />
            </button>
            <GateHeading className="mt-4">Your details</GateHeading>
            <GateSubcopy>{summary}</GateSubcopy>
            <form
                className="mt-6 flex flex-1 flex-col gap-5.5"
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isSending) onSubmit();
                }}
            >
                <GateIdentityFields
                    identity={identity}
                    expanded={expanded}
                    onExpandedChange={(next) => {
                        touchedRef.current = true;
                        setExpanded(next);
                    }}
                    disabled={isSending}
                    onSwitchAccount={onSwitchAccount}
                >
                    <div className="grid grid-cols-2 gap-1.5">
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel htmlFor="book-meeting-first-name">
                                First Name
                            </GateFieldLabel>
                            <GateInput
                                id="book-meeting-first-name"
                                placeholder="First Name"
                                autoComplete="given-name"
                                editHint
                                disabled={isSending}
                                value={contact.firstName}
                                onChange={set('firstName')}
                            />
                            <GateFieldError message={errors.firstName} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel htmlFor="book-meeting-last-name">
                                Last Name
                            </GateFieldLabel>
                            <GateInput
                                id="book-meeting-last-name"
                                placeholder="Last Name"
                                autoComplete="family-name"
                                editHint
                                disabled={isSending}
                                value={contact.lastName}
                                onChange={set('lastName')}
                            />
                            <GateFieldError message={errors.lastName} />
                        </div>
                    </div>
                    <div
                        className={cn(
                            'grid gap-1.5',
                            isDesktop ? 'grid-cols-2' : 'grid-cols-1',
                        )}
                    >
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel htmlFor="book-meeting-email">
                                Email Address
                            </GateFieldLabel>
                            <GateInput
                                id="book-meeting-email"
                                type="email"
                                placeholder="you@email.com"
                                autoComplete="email"
                                autoCapitalize="off"
                                editHint={!emailLocked}
                                className={cn(emailLocked && 'opacity-60')}
                                disabled={isSending || emailLocked}
                                value={contact.email}
                                onChange={set('email')}
                            />
                            <GateFieldError message={errors.email} />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel htmlFor="book-meeting-phone">
                                Phone
                            </GateFieldLabel>
                            <GateInput
                                id="book-meeting-phone"
                                type="tel"
                                placeholder="Phone number"
                                autoComplete="tel"
                                editHint
                                disabled={isSending}
                                value={contact.phone}
                                onChange={set('phone')}
                            />
                            <GateFieldError message={errors.phone} />
                        </div>
                    </div>
                </GateIdentityFields>

                {!identity ? (
                    <p className="m-0 text-[0.75rem] leading-relaxed tracking-[-0.02em] text-[#828282]">
                        We&rsquo;ll sign you in to ArchiPro with this email, so
                        you can manage your meeting and talk to the team.
                    </p>
                ) : null}

                {errorMessage ? (
                    <p
                        role="alert"
                        className="m-0 text-[0.8125rem] text-destructive"
                    >
                        {errorMessage}
                    </p>
                ) : null}

                <GateStickyFooter pinOn="both" className="mt-auto">
                    <button
                        type="submit"
                        disabled={isSending}
                        className={cn(
                            GATE_CTA_CLASS,
                            'gap-2.5 disabled:cursor-not-allowed disabled:opacity-60',
                        )}
                    >
                        {isSending ? (
                            <>
                                <Loader2 className="size-5 animate-spin" />
                                {isBooked ? 'Booking…' : 'Sending…'}
                            </>
                        ) : isBooked ? (
                            'Book meeting'
                        ) : (
                            'Send request'
                        )}
                    </button>
                </GateStickyFooter>
            </form>
        </div>
    );
};
